import { BookOpenIcon, ChevronDownIcon, CompassIcon, CopyIcon, ImportIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { Entity, EntityType, usePrompt } from "@/queries";
import { PromptBlueprint } from "@/types";
import { displayToast } from "@/utils/toast";
import { ChooseEntityDialogControlled } from "@/components/ChooseEntityDialog";

const DOCS_URL = "https://docs.promptlayer.com/";
const SNIPPET_DOCS_URL = "https://docs.promptlayer.com/features/prompt-registry/snippets";

interface PromptObjectMenuProps {
  title: string;
  isSnippet: boolean;
  blueprint?: PromptBlueprint | null;
  onApplyBlueprint: (prompt: Partial<PromptBlueprint>) => void;

  snippetText?: string;
}

export default function PromptObjectMenu({
  title,
  isSnippet,
  blueprint,
  onApplyBlueprint,

  snippetText,
}: PromptObjectMenuProps) {
  const [isLoadDialogOpen, setIsLoadDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Entity<
    EntityType.PROMPT | EntityType.SNIPPET
  > | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const appliedSelectionRef = useRef<string | null>(null);

  const userToken = useUserToken();
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const { data, isLoading, isFetching, error } = usePrompt(userToken, {
    workspace_id: workspaceId,
    prompt_name: selectedTemplate?.name,
    number: selectedTemplate?.version_number,
  });

  const copyPayload = useMemo(
    () => snippetText ?? JSON.stringify(blueprint, null, 2),
    [blueprint, snippetText],
  );

  useEffect(() => {
    if (!selectedTemplate || isLoading || isFetching || !data || error) {
      return;
    }

    const selectionKey = `${selectedTemplate.name}:${selectedTemplate.version_number ?? "latest"}`;
    if (appliedSelectionRef.current === selectionKey) {
      return;
    }

    appliedSelectionRef.current = selectionKey;
    const appliedTemplate = selectedTemplate;
    setSelectedTemplate(null);
    onApplyBlueprint(data as Partial<PromptBlueprint>);
    displayToast(
      `Loaded ${appliedTemplate.name} (${appliedTemplate.version_number ? `Version #${appliedTemplate.version_number}` : "Latest"})`,
      ToastType.success,
    );
  }, [data, error, isFetching, isLoading, onApplyBlueprint, selectedTemplate]);

  useEffect(() => {
    if (!selectedTemplate || isFetching || !error) {
      return;
    }

    displayToast("Failed to load prompt data", ToastType.error);
    setSelectedTemplate(null);
  }, [error, isFetching, selectedTemplate]);

  const handleLoadSelection = (entity: Entity<EntityType.PROMPT | EntityType.SNIPPET>) => {
    appliedSelectionRef.current = null;
    setSelectedTemplate(entity);
    setIsLoadDialogOpen(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyPayload);
      displayToast(isSnippet ? "Snippet copied" : "Prompt JSON copied", ToastType.success);
    } catch {
      displayToast("Unable to copy to clipboard", ToastType.error);
    }
  };

  const handleStartTour = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("tour", "");
    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="group h-8 min-w-0 items-center gap-1.5 rounded-md px-2 text-gray-950 hover:bg-gray-100"
          >
            <span className="min-w-0 truncate text-base font-semibold leading-5">{title}</span>
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-gray-500" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          className="w-60 rounded-lg border-gray-200 p-1 shadow-lg"
        >
          <DropdownMenuItem
            onSelect={() => setIsLoadDialogOpen(true)}
            className="h-9 cursor-pointer rounded-md text-gray-700"
          >
            <ImportIcon className="h-4 w-4 text-gray-400" />
            <span>{isSnippet ? "Load snippet" : "Load prompt"}</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={handleCopy}
            className="h-9 cursor-pointer rounded-md text-gray-700"
          >
            <CopyIcon className="h-4 w-4 text-gray-400" />
            <span>{isSnippet ? "Copy snippet" : "Copy JSON"}</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onSelect={() => window.open(isSnippet ? SNIPPET_DOCS_URL : DOCS_URL, "_blank")}
            className="h-9 cursor-pointer rounded-md text-gray-700"
          >
            <BookOpenIcon className="h-4 w-4 text-gray-400" />
            <span>Docs</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={handleStartTour}
            className="h-9 cursor-pointer rounded-md text-gray-700"
          >
            <CompassIcon className="h-4 w-4 text-gray-400" />
            <span>Take tour</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChooseEntityDialogControlled
        open={isLoadDialogOpen}
        onOpenChange={open => {
          setIsLoadDialogOpen(open);
          if (open) {
            appliedSelectionRef.current = null;
            setSelectedTemplate(null);
          }
        }}
        config={{
          selectLatest: false,
        }}
        type={isSnippet ? EntityType.SNIPPET : EntityType.PROMPT}
        onSelect={handleLoadSelection}
        currentFolderId={null}
      />
    </>
  );
}
